import { Box, HStack, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import ChevronDownIcon from '~icons/lucide/chevron-down';
import MaximizeIcon from '~icons/lucide/maximize-2';
import MinimizeIcon from '~icons/lucide/minimize-2';
import PhoneOffIcon from '~icons/lucide/phone-off';
import PipIcon from '~icons/lucide/picture-in-picture-2';
import CloseIcon from '~icons/lucide/x';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useChatSettingsStore } from '#modules/chat/model/chatSettingsStore';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { CallMini } from '#features/calls/components/CallOverlay/CallMini';
import { CallStage } from '#features/calls/components/CallOverlay/CallStage';
import { RingScreen } from '#features/calls/components/CallOverlay/RingScreen';
import { useCallActions } from '#features/calls/hooks/useCallActions';
import { useCallRoom } from '#features/calls/hooks/useCallRoom';
import { callReachQueryKey, type CallReachAction } from '#features/calls/model/callPolicy';
import { useCallStore } from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import {
  isDocumentPipSupported,
  openCallPipWindow,
  type CallPipHandle,
} from '#features/calls/utils/callPipWindow';
import { startRingtone, stopRingtone } from '#features/calls/utils/callRingtone';
import { isNativeCallEnabled, setCallMicrophoneEnabled } from '#features/calls/utils/nativeCalls';
import { shouldRingIncomingCall } from '#features/calls/utils/ringPolicy';
import { formatElapsed } from '#features/date';
import {
  CORNER_PROPS,
  MOBILE_ANCHOR_PROPS,
  MOBILE_BAR_BOTTOM,
  MOBILE_BAR_TOP,
  MOBILE_WIDGET_MAX_HEIGHT,
  VOICE_BAR_RESERVE,
} from '#features/media-window/model';
import { useCornerDrag } from '#features/media-window/useCornerDrag';
import { useScrollTouchAction } from '#features/media-window/useScrollTouchAction';
import { dismissCallNotification } from '#features/notifications/utils/deviceSettings';
import { onWsReconnect } from '#features/realtime/hooks/useRealtimeNotifications';
import { Avatar } from '#features/ui/components/Avatar';
import { Button } from '#features/ui/components/Button';
import { IconButton } from '#features/ui/components/IconButton';
import { Portal } from '#features/ui/components/Portal';
import { toaster } from '#features/ui/components/Toaster';
import { useVoiceActions } from '#features/voice/hooks/useVoiceActions';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { envConfig } from '#constants/envConfig';
import { useBottomSpaceVar } from '#hooks/useBottomSpaceVar';
import { useIsChatThread } from '#hooks/useIsChatThread';

/**
 * Long enough to read the note *and* act on it: the note now offers a call back, and 2.5s
 * was under the time it takes to notice a button appeared.
 */
const ENDED_DISMISS_MS = 6_000;

/**
 * Та же плашка, но с двумя выходами: перезвонить и вернуться в голос сессии. Выбор из двух
 * читается дольше одного, а удержание по наведению — единственная страховка от исчезновения —
 * на телефоне не работает вовсе: там курсора нет.
 */
const ENDED_WITH_VOICE_DISMISS_MS = 12_000;

/**
 * Frosted materials are cheap on iOS and on desktop, and expensive in Android's WebView when
 * they sit over a scrolling feed — which is exactly where the mini pill lives. On native the
 * pill is over an opaque app background anyway, so the blur buys nothing there.
 */
const PILL_BACKDROP = envConfig.IS_NATIVE ? undefined : 'blur(20px) saturate(1.3)';

/**
 * Панели «звонок на другом устройстве» сворачиваться некуда — свёрнутого вида у неё нет, — поэтому
 * рывок вниз от нижнего края просто оставляет её на месте. Константа, а не стрелка в теле: жест
 * запоминается по ссылкам на колбэки, и новая функция на каждый рендер пересобирала бы обработчик.
 */
const NO_COLLAPSE = () => {};

const formatDuration = (fromMs: number, nowMs: number): string =>
  formatElapsed((nowMs - fromMs) / 1000);

const useCallDuration = (acceptedAt: number | null, enabled: boolean): string => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  if (!enabled || !acceptedAt) {
    return '00:00';
  }
  return formatDuration(acceptedAt, now);
};

/**
 * Global 1:1 call UI: incoming/outgoing ring, in-call window (fullscreen on mobile,
 * draggable-to-corner floating window on desktop, collapsible into a mini pill) and
 * a brief "call ended" note. Mounted once in the locale layout next to the WS hook.
 * Media (SDP/ICE/TURN/reconnects) is handled by LiveKit via useCallRoom.
 */
export const CallOverlay = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const isAuth = useIsAuth();
  const { profile } = useProfile();
  const selfAvatar = toCallAvatarUrl(profile?.avatar);
  const phase = useCallStore((s) => s.phase);
  const call = useCallStore((s) => s.call);
  const endReason = useCallStore((s) => s.endReason);
  const micEnabled = useCallStore((s) => s.micEnabled);
  const camEnabled = useCallStore((s) => s.camEnabled);
  const windowMode = useCallStore((s) => s.windowMode);
  // Единственное, что звонок знает про голос сессии: идёт ли там разговор прямо сейчас. Ответ на
  // звонок из комнаты выводит (арбитраж живёт в features/voice), а до ответа ринг обязан вести
  // себя скромнее — иначе он отбирает управление чужим разговором.
  const inVoiceRoom = useVoiceStore((s) => s.phase !== 'idle');
  /**
   * Комната, из которой человека вынул этот звонок. Сами туда никого не возвращаем — решение
   * сознательное (см. useVoiceCallArbitration): вернуть в общий разговор без спроса значит
   * включить человеку микрофон при команде. Но путь обратно обязан быть виден.
   */
  const interruptedVoice = useVoiceStore((s) => s.interruptedRoom);
  const forgetInterruptedVoice = useVoiceStore((s) => s.forgetInterruptedRoom);
  const corner = useCallStore((s) => s.corner);
  const mobileAnchor = useCallStore((s) => s.mobileAnchor);
  const setCamEnabled = useCallStore((s) => s.setCamEnabled);
  const setWindowMode = useCallStore((s) => s.setWindowMode);
  const setCorner = useCallStore((s) => s.setCorner);
  const setMobileAnchor = useCallStore((s) => s.setMobileAnchor);

  const { answer, claim, dismissHere, hangup, resync, start } = useCallActions();
  const { join: joinVoice } = useVoiceActions();
  const room = useCallRoom();
  const { connectionState, peerGone, attachPipVideo, detachPipVideo } = room;

  const duration = useCallDuration(call?.acceptedAt ?? null, phase === 'active');
  const windowRef = useRef<HTMLDivElement | null>(null);
  // The mini pill needs both: a ref for the drag gesture and a node in state, so the effect that
  // publishes the space it takes re-runs when the pill appears and disappears.
  const pillRef = useRef<HTMLDivElement | null>(null);
  const [pillNode, setPillNode] = useState<HTMLDivElement | null>(null);
  const attachPill = useCallback((node: HTMLDivElement | null) => {
    pillRef.current = node;
    setPillNode(node);
  }, []);
  // Панель «звонок идёт на другом устройстве» переносится тем же жестом, что и пилюля, поэтому
  // ей нужна своя ссылка на узел: жест двигает именно тот элемент, за который взялись.
  const elsewhereRef = useRef<HTMLDivElement | null>(null);
  const pipRef = useRef<CallPipHandle | null>(null);
  const [endedHovered, setEndedHovered] = useState(false);
  const [endedNode, setEndedNode] = useState<HTMLDivElement | null>(null);
  // Список устройств живёт в шапке, а открывать его умеет и кнопка микрофона из панели снизу —
  // поэтому состояние держит общий родитель, а не сама панель.
  const [deviceMenuOpen, setDeviceMenuOpen] = useState(false);
  // The duration timer stops with the call, so the "ended" note would report 00:00 unless
  // the last value is kept.
  const lastDurationRef = useRef('00:00');

  /**
   * Answering a video call without the camera is still answering: the ring screen offers it
   * as a separate action, and all it means is that the local camera track starts off.
   */
  const handleAnswer = useCallback(
    (options?: { video: boolean }) => {
      if (options && !options.video) {
        setCamEnabled(false);
      }
      void answer();
    },
    [answer, setCamEnabled]
  );

  /*
    Звонок на телефоне – плавающая карточка, а не весь экран (01.10.2026, решение владельца:
    «фулл экран пока не надо, пока виджетом сделаем»). Та же карточка, что у комнаты сессии:
    стоит у нижнего или верхнего края над навигацией, переносится между ними за любое место,
    рывок вниз от нижнего края сворачивает её в пилюлю. В переписке край свой – по умолчанию
    верх, под шапкой, подальше от поля ввода, – и переносится тоже (02.10.2026: запертая карточка
    мешала, как и запертая комната сессии).
  */
  const isPhone = useBreakpointValue({ base: true, md: false }) ?? false;
  const inChatThread = useIsChatThread();
  const chatAnchor = useCallStore((s) => s.chatAnchor);
  const setChatAnchor = useCallStore((s) => s.setChatAnchor);
  const cardAnchor = inChatThread ? chatAnchor : mobileAnchor;
  /*
    Полоса «Звонок завершён» вне переписки всегда внизу, как и была. В переписке внизу поле ввода,
    и она встаёт под шапку – там же, где стояла карточка звонка (край `chatAnchor`, по умолчанию верх).
  */
  const endedAtTop = inChatThread && chatAnchor === 'top';
  const cardScroller = useScrollTouchAction<HTMLDivElement>(
    isPhone && phase === 'active' && windowMode !== 'mini'
  );

  // Desktop: the window follows the pointer live by its grip strip and snaps to the nearest
  // corner on release; fullscreen isn't draggable, hence the mode check. Phone: see above.
  const onHeaderPointerDown = useCornerDrag({
    ref: windowRef,
    canDrag: windowMode === 'window',
    anchor: cardAnchor,
    onCollapse: () => setWindowMode('mini'),
    onCorner: setCorner,
    onAnchor: inChatThread ? setChatAnchor : setMobileAnchor,
  });

  /**
   * The pill has its own gesture. It used to have none, and that was the whole problem: on a phone
   * the pill sits at the bottom edge, which in a chat thread is exactly where the message composer
   * is. The call could not be moved and hiding it is not an option — closing it would mean hanging
   * up — so the only way to write was to end the conversation.
   */
  const onPillPointerDown = useCornerDrag({
    ref: pillRef,
    canDrag: false,
    anchor: cardAnchor,
    onCollapse: () => setWindowMode('mini'),
    onCorner: setCorner,
    onAnchor: inChatThread ? setChatAnchor : setMobileAnchor,
  });

  /**
   * Та же ручка у панели «звонок идёт на другом устройстве». Панель встаёт на место пилюли — в
   * чате это ровно поле ввода, — но подвинуть её было нечем: оставалось либо оборвать разговор,
   * который в эту секунду идёт на другом устройстве, либо убрать панель совсем и вместе с ней
   * единственную кнопку «перенести сюда».
   */
  const onElsewherePointerDown = useCornerDrag({
    ref: elsewhereRef,
    canDrag: false,
    anchor: cardAnchor,
    onCollapse: NO_COLLAPSE,
    onCorner: setCorner,
    onAnchor: inChatThread ? setChatAnchor : setMobileAnchor,
  });

  /*
    The page leaves room for the pill the same way it does for the bottom navigation — but only
    while the pill is actually at the bottom. Raised to the top edge it covers nothing.

    Полоса «Звонок завершён» просит место на тех же правах. Раньше не просила, и на телефоне
    ложилась прямо поверх поля ввода переписки: подсказка «Напишите сообщение» и стрелка
    отправки просвечивали из-под неё, а ответить было нельзя, пока полоса не исчезнет сама.
    Фазы «идёт звонок» и «звонок завершён» не бывают одновременно, поэтому переменная одна.
  */
  const bottomBarNode =
    phase === 'active' && windowMode === 'mini' && cardAnchor === 'bottom'
      ? pillNode
      : phase === 'ended' && !endedAtTop
        ? endedNode
        : null;
  useBottomSpaceVar('--voice-bar-space', bottomBarNode);

  // Meet-style always-on-top widget (Document PiP, Chrome/Edge desktop). The PiP
  // window mirrors the peer's stream and basic controls; audio keeps playing in
  // this tab. Closed automatically when the call ends.
  const openPip = useCallback(async () => {
    const currentCall = useCallStore.getState().call;
    if (pipRef.current || !currentCall) {
      return;
    }
    const peerName = currentCall.peer.displayName || t`Player`;
    const handle = await openCallPipWindow({
      peerName,
      peerAvatarUrl: currentCall.peer.avatar,
      attachVideo: attachPipVideo,
      detachVideo: detachPipVideo,
      getDurationLabel: () => {
        const acceptedAt = useCallStore.getState().call?.acceptedAt;
        return acceptedAt ? formatDuration(acceptedAt, Date.now()) : '00:00';
      },
      isMicEnabled: () => useCallStore.getState().micEnabled,
      onToggleMic: () => {
        const state = useCallStore.getState();
        setCallMicrophoneEnabled(!state.micEnabled);
      },
      onHangup: () => hangup(),
      onExpand: () => window.focus(),
      onClosed: () => {
        pipRef.current = null;
      },
      labels: {
        mic: t`Microphone`,
        hangup: t`End call`,
        expand: t`Back to Teamplay`,
        sources: {
          remoteScreen: t`${peerName} - screen`,
          remoteCam: peerName,
          localScreen: t`You - screen`,
          localCam: t`You`,
        },
      },
    });
    pipRef.current = handle;
  }, [attachPipVideo, detachPipVideo, hangup, t]);

  useEffect(() => {
    if (phase !== 'active' && pipRef.current) {
      pipRef.current.close();
      pipRef.current = null;
    }
  }, [phase]);

  /*
    Автоматический PiP при уходе со вкладки.

    Одного обработчика `enterpictureinpicture` мало, и это стоило нам работающей возможности:
    браузер зовёт его только у владельца ЖИВОЙ медиасессии, а сессия считается живой, когда у
    неё есть метаданные и состояние `playing`. Мы не ставили ни того, ни другого – элемент
    `<audio>` со звуком собеседника есть, а сессии для браузера нет, и обработчик не звали ни
    разу. Заодно метаданные – это то, что человек видит в системной шторке мультимедиа: без них
    там пусто.

    Только там, где есть окошко поверх окон (Chrome и Edge на компьютере). На iPhone то же
    сообщение «играет медиа» система рисовала музыкальным плеером на заблокированном экране – с
    перемоткой на 10 секунд и паузой, у звонка бессмысленными (02.10.2026). На телефоне разговор
    показывает своя табличка оболочки: «идёт звонок» на Android, карточка голоса на iPhone.
  */
  const peerNameForSession = call?.peer.displayName || t`Player`;

  useEffect(() => {
    if (phase !== 'active' || !('mediaSession' in navigator) || !isDocumentPipSupported()) {
      return;
    }

    const previousMetadata = navigator.mediaSession.metadata;
    const previousState = navigator.mediaSession.playbackState;

    try {
      if (typeof MediaMetadata !== 'undefined') {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: peerNameForSession,
          artist: 'Teamplay',
        });
      }
      navigator.mediaSession.playbackState = 'playing';
    } catch {
      // Метаданные – украшение: не вышло, значит идём дальше без них.
    }

    let handlerSet = false;
    const action = 'enterpictureinpicture' as MediaSessionAction;
    try {
      navigator.mediaSession.setActionHandler(action, () => void openPip());
      handlerSet = true;
    } catch {
      // Действия нет в этом браузере – обычное дело вне Chrome.
    }

    return () => {
      try {
        navigator.mediaSession.metadata = previousMetadata;
        navigator.mediaSession.playbackState = previousState;
        if (handlerSet) {
          navigator.mediaSession.setActionHandler(action, null);
        }
      } catch {
        // ignore
      }
    };
  }, [phase, openPip, peerNameForSession]);

  // Ring while a call is ringing (respects the global sound toggle; the per-chat mute is
  // deliberately not part of the decision — see shouldRingIncomingCall).
  useEffect(() => {
    if (isNativeCallEnabled() && phase === 'incoming') {
      stopRingtone();
      return;
    }
    const { soundEnabled, isChatMuted } = useChatSettingsStore.getState();
    const silent = !shouldRingIncomingCall({
      soundEnabled,
      chatMuted: isChatMuted(call?.chatId),
    });

    if (phase === 'incoming') {
      // Once this overlay is on screen it owns the call — including the sound. The system
      // notification that woke the phone is the other ringtone, so it is silenced only after
      // ours is audibly playing: dismissing it first left a call ringing in total silence.
      const ringingCallId = call?.callId;
      // While the phone is locked or the app is in the background, the system notification is
      // the ring — it is also what wakes the screen. We only take the sound over once this
      // screen is actually visible, and only then silence the notification; doing it earlier
      // left the phone dark and, before that, silent.
      const ring = () => {
        if (silent) {
          // Звуки приложения выключены целиком — своего звонка не будет. Уведомление всё равно
          // гасим: экран входящего уже перед человеком, и уведомление лишь дублирует его. Это то
          // же место и то же условие, что и у звонящей ветки ниже.
          if (ringingCallId) {
            void dismissCallNotification(ringingCallId);
          }
          return;
        }
        void startRingtone('incoming').then((playing) => {
          if (playing && ringingCallId) {
            void dismissCallNotification(ringingCallId);
          }
          return playing;
        });
      };
      if (document.hidden) {
        document.addEventListener('visibilitychange', ring, { once: true });
      } else {
        ring();
      }
      return () => {
        document.removeEventListener('visibilitychange', ring);
        stopRingtone();
      };
    }
    if (phase === 'outgoing') {
      if (soundEnabled) {
        void startRingtone('outgoing');
      }
      return () => stopRingtone();
    }
    stopRingtone();
  }, [phase, call?.callId, call?.chatId]);

  useEffect(() => {
    if (phase === 'active') {
      lastDurationRef.current = duration;
    }
  }, [phase, duration]);

  // The note can unmount from under the pointer — clicking its own "Call back" is exactly
  // that — and no mouseleave ever arrives. Without this reset the hover flag stays latched
  // and every later note hangs around forever.
  useEffect(() => {
    if (phase !== 'ended') {
      setEndedHovered(false);
    }
  }, [phase]);

  // Память о покинутой комнате нужна ровно одной плашке. Плашка ушла — предложение протухло: к
  // следующему звонку та сессия успеет кончиться, а звать в мёртвую комнату хуже, чем молчать.
  useEffect(() => {
    if (phase === 'idle') {
      forgetInterruptedVoice();
    }
  }, [phase, forgetInterruptedVoice]);

  // Auto-dismiss the "ended" note — held open while the pointer is on it, so the call-back
  // button cannot disappear from under the cursor reaching for it.
  useEffect(() => {
    if (phase !== 'ended' || endedHovered) {
      return;
    }
    const timer = window.setTimeout(
      () => useCallStore.getState().reset(),
      interruptedVoice ? ENDED_WITH_VOICE_DISMISS_MS : ENDED_DISMISS_MS
    );
    return () => window.clearTimeout(timer);
  }, [phase, endedHovered, interruptedVoice]);

  // State resync: on mount (reload mid-call) and after every WS reconnect.
  useEffect(() => {
    if (!isAuth) {
      return;
    }
    void resync();
    return onWsReconnect(() => void resync());
  }, [isAuth, resync]);

  // iOS Safari freezes background tabs mid-call-setup: the user accepts, the tab
  // gets suspended before room.connect, and minutes later the thawed tab replays a
  // stale connect (seen in the wild as /rtc/validate with a long-dead room token).
  // Resync against the server the moment the tab becomes visible again.
  useEffect(() => {
    if (!isAuth) {
      return;
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void resync();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuth, resync]);

  if (!isAuth || phase === 'idle' || !call) {
    return null;
  }

  const peerName = call.peer.displayName || t`Player`;

  const endedText = (() => {
    switch (endReason) {
      case 'declined':
        return t`Call declined`;
      case 'no_answer':
        return t`No answer`;
      case 'missed':
      case 'cancelled':
        return t`Call ended`;
      case 'answered_elsewhere':
        return t`Answered on another device`;
      case 'peer_left':
        return t`${peerName} left the call`;
      case 'connect_failed':
        return t`Could not connect to the call`;
      default:
        return t`Call ended`;
    }
  })();

  const statusText =
    connectionState === 'reconnecting'
      ? t`Reconnecting…`
      : connectionState === 'connecting'
        ? t`Connecting…`
        : peerGone
          ? t`${peerName} is reconnecting…`
          : duration;

  return (
    <Portal>
      {(phase === 'incoming' || phase === 'outgoing') && (
        <RingScreen
          call={call}
          phase={phase}
          peerName={peerName}
          onAnswer={handleAnswer}
          onHangup={() => hangup(phase === 'incoming' ? 'declined' : 'cancelled')}
          // Человек уже разговаривает с командой — тогда звонок просится, а не врывается.
          compact={inVoiceRoom}
        />
      )}

      {phase === 'active' && windowMode === 'mini' && (
        <CallMini
          peerName={peerName}
          peerAvatar={call.peer.avatar}
          statusText={statusText}
          room={room}
          micEnabled={micEnabled}
          anchor={isPhone ? cardAnchor : 'bottom'}
          onToggleMic={() => setCallMicrophoneEnabled(!micEnabled)}
          onHangup={() => hangup('ended')}
          onExpand={() => setWindowMode('window')}
          onNodeChange={attachPill}
          onGripPointerDown={onPillPointerDown}
        />
      )}

      {phase === 'active' && windowMode !== 'mini' && (
        <Box
          ref={windowRef}
          data-testid="call-window"
          data-voice-device-anchor={isPhone || windowMode === 'window' ? '' : undefined}
          data-layout={isPhone ? 'card' : windowMode}
          data-anchor={isPhone ? cardAnchor : undefined}
          position="fixed"
          zIndex="modal"
          overflow="hidden"
          boxShadow="2xl"
          display="flex"
          flexDirection="column"
          bg="bg"
          {...(isPhone
            ? {
                // Полноэкранного режима на телефоне нет: и «окно», и «весь экран» – карточка.
                left: 3,
                right: 3,
                ...MOBILE_ANCHOR_PROPS[cardAnchor],
                maxH: MOBILE_WIDGET_MAX_HEIGHT,
                borderRadius: 'l3',
                cursor: 'grab',
                onPointerDown: onHeaderPointerDown,
                // Без этого палец прокручивает страницу под карточкой, и перенос не начинается.
                css: { touchAction: 'none', overscrollBehavior: 'none' },
              }
            : {
                ...(windowMode === 'full' ? { inset: 0 } : CORNER_PROPS[corner]),
                borderRadius: windowMode === 'full' ? 'none' : 'xl',
                ...(windowMode === 'window'
                  ? {
                      w: '400px',
                      h: 'auto',
                      css: {
                        overscrollBehavior: 'none',
                        resize: 'both',
                        overflow: 'hidden',
                        minWidth: '340px',
                        minHeight: '220px',
                        maxWidth: '80vw',
                        maxHeight: '85vh',
                      },
                    }
                  : { css: { overscrollBehavior: 'none' } }),
              })}
        >
          {/* Thin grab strip: drag the window to any screen corner (desktop, window mode). On a
              phone there is none – the whole card is the handle, and a grip bar made it read as
              a bottom sheet. */}
          {isPhone ? null : (
            <Box
              h="14px"
              flexShrink={0}
              bg="bg"
              cursor={windowMode === 'window' ? 'grab' : 'default'}
              onPointerDown={onHeaderPointerDown}
              userSelect="none"
              css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box w="36px" h="4px" borderRadius="full" bg="whiteAlpha.400" />
            </Box>
          )}
          <Box
            flex={1}
            minH={0}
            display="flex"
            flexDirection="column"
            // Карточка ниже потолка высоты: длинное содержимое (видео, подсказки) прокручивается
            // внутри, шапка с кнопками уезжает вместе с ним, а не обрезается.
            overflowY={isPhone ? 'auto' : undefined}
            {...(isPhone
              ? { ref: cardScroller.ref, css: { touchAction: cardScroller.touchAction } }
              : {})}
          >
            <CallStage
              deviceMenuOpen={deviceMenuOpen}
              onDeviceMenuOpenChange={setDeviceMenuOpen}
              call={call}
              variant="floating"
              phase="active"
              duration={duration}
              micEnabled={micEnabled}
              camEnabled={camEnabled}
              onToggleMic={() => setCallMicrophoneEnabled(!micEnabled)}
              onToggleCam={() => setCamEnabled(!camEnabled)}
              onHangup={() => hangup('ended')}
              room={room}
              selfAvatar={selfAvatar}
              fill={!isPhone && windowMode === 'full'}
              headerExtra={
                <>
                  {isDocumentPipSupported() && (
                    <IconButton
                      aria-label={t`Pop out call widget`}
                      size="sm"
                      minW="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="transparent"
                      color="fg.secondary"
                      _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                      onClick={() => void openPip()}
                    >
                      <PipIcon />
                    </IconButton>
                  )}
                  {/* Разворачивать нечего на телефоне: там окно звонка и так занимает экран
                      целиком в обоих режимах, и кнопка была молчаливой пустышкой. */}
                  {windowMode === 'window' ? (
                    <IconButton
                      aria-label={t`Enter fullscreen`}
                      size="sm"
                      minW="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="transparent"
                      color="fg.secondary"
                      display={{ base: 'none', md: 'inline-flex' }}
                      _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                      onClick={() => setWindowMode('full')}
                    >
                      <MaximizeIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      aria-label={t`Exit fullscreen`}
                      size="sm"
                      minW="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="transparent"
                      color="fg.secondary"
                      display={{ base: 'none', md: 'inline-flex' }}
                      _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                      onClick={() => setWindowMode('window')}
                    >
                      <MinimizeIcon />
                    </IconButton>
                  )}
                  <IconButton
                    aria-label={t`Minimize call`}
                    size="sm"
                    minW="40px"
                    h="40px"
                    borderRadius="lg"
                    bg="transparent"
                    color="fg.secondary"
                    _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                    onClick={() => setWindowMode('mini')}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                </>
              }
            />
          </Box>
        </Box>
      )}

      {/* Missed and declined are the common outcomes at this stage, so the note is also the
          place to try again — otherwise the call ends in a dead end. */}
      {/* Звонок идёт на другом устройстве этого же человека. Подключиться отсюда нельзя — второй
          клиент выбил бы первого из комнаты, и собеседник потерял бы разговор, — поэтому вместо
          тихой драки за комнату предлагаем перенести его сюда, как это делает плеер с колонкой.
          Панель на месте пилюли: и в браузере, и в приложении она попадает в один и тот же угол. */}
      {phase === 'elsewhere' && (
        <HStack
          ref={elsewhereRef}
          data-anchor={cardAnchor}
          position="fixed"
          // Край на телефоне — тот же, что у карточки и пилюли (в переписке свой): разговор один, и место на
          // экране у него одно. `auto` с противоположной стороны обязателен, иначе панель
          // растягивается от верха до низа.
          top={{ base: cardAnchor === 'top' ? MOBILE_BAR_TOP : 'auto', md: 'auto' }}
          bottom={{
            base: cardAnchor === 'top' ? 'auto' : MOBILE_BAR_BOTTOM,
            md: 4,
          }}
          left={{ base: 4, md: 'auto' }}
          right={4}
          zIndex="modal"
          bg="bg.surface/92"
          backdropFilter={PILL_BACKDROP}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor="border"
          boxShadow="xl"
          pl={3}
          pr={2.5}
          py={2.5}
          gap={3}
        >
          {/* Аватар с именем — ручка панели, ровно как у пилюли. Не вся панель целиком:
              `preventDefault` на pointerdown отобрал бы нажатия у «Move here», трубки и крестика
              рядом. */}
          <HStack
            gap={3}
            minW={0}
            flex={1}
            userSelect="none"
            onPointerDown={onElsewherePointerDown}
            css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
          >
            <Avatar
              size="xs"
              src={call.peer.avatar}
              name={peerName}
              borderRadius="lg"
              css={{ '--avatar-radius': 'var(--chakra-radii-lg)' }}
            />
            <Stack gap={0} minW={0} flex={1}>
              <Text textStyle="label" lineClamp={1}>
                {peerName}
              </Text>
              <Text textStyle="caption" color="fg.muted" lineClamp={1}>
                {t`Call is open on another device`}
              </Text>
            </Stack>
          </HStack>
          <Button
            size="sm"
            minH="40px"
            variant="outline"
            borderRadius="xl"
            flexShrink={0}
            onClick={() => void claim()}
          >
            {t`Move here`}
          </Button>
          <IconButton
            aria-label={t`End call for everyone`}
            size="sm"
            minW="40px"
            h="40px"
            colorPalette="red"
            borderRadius="xl"
            onClick={() => hangup('ended')}
          >
            <PhoneOffIcon />
          </IconButton>
          {/* Третий, безобидный выход: убрать панель именно здесь. Без него единственным способом
              избавиться от неё была красная трубка — то есть оборвать разговор, который в эту
              секунду идёт на другом устройстве. */}
          <IconButton
            aria-label={t`Hide on this device`}
            size="sm"
            minW="32px"
            h="40px"
            bg="transparent"
            color="fg.muted"
            borderRadius="xl"
            _hover={{ bg: 'bg.emphasized', color: 'fg' }}
            onClick={dismissHere}
          >
            <CloseIcon />
          </IconButton>
        </HStack>
      )}

      {phase === 'ended' && (
        <HStack
          position="fixed"
          /*
            Пока я в голосе, плашка поднимается над его полосой. Место у нижнего края одно, и
            «Звонок завершён» на шесть секунд накрывало полосу голоса — единственный орган
            управления комнатой на телефоне: кто говорит, микрофон, выход. Особенно нелепо это
            выглядит рядом с кнопкой «вернуться в голос», которая на этой же плашке и живёт.
          */
          top={{ base: endedAtTop ? MOBILE_BAR_TOP : 'auto', md: 'auto' }}
          bottom={{
            base: endedAtTop
              ? 'auto'
              : inVoiceRoom
                ? `calc(${MOBILE_BAR_BOTTOM} + ${VOICE_BAR_RESERVE})`
                : MOBILE_BAR_BOTTOM,
            md: 6,
          }}
          left={{ base: 4, md: '50%' }}
          right={{ base: 4, md: 'auto' }}
          transform={{ md: 'translateX(-50%)' }}
          zIndex="modal"
          bg="bg.surface/92"
          backdropFilter={PILL_BACKDROP}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor="border"
          boxShadow="xl"
          pl={4}
          pr={2.5}
          py={2.5}
          gap={4}
          // На узком телефоне подпись и две кнопки в строку не помещаются — пусть ряд кнопок
          // переносится вниз, а не сдавливает текст до многоточия из одной буквы.
          flexWrap="wrap"
          rowGap={2}
          ref={setEndedNode}
          data-anchor={endedAtTop ? 'top' : 'bottom'}
          /*
            Удержание — страховка для мыши: пока на полосу наведён курсор, она не исчезает.
            Палец курсором не считается: браузер шлёт «наведение» и на касание, и полоса,
            тронутая пальцем, зависала бы навсегда — а закрыть её было нечем.
          */
          onPointerEnter={(event: React.PointerEvent) => {
            if (event.pointerType === 'mouse') setEndedHovered(true);
          }}
          onPointerLeave={(event: React.PointerEvent) => {
            if (event.pointerType === 'mouse') setEndedHovered(false);
          }}
        >
          {/* flex-basis, а не голый flex={1}: с двумя кнопками ряду нужно право уехать на вторую
              строку, а подписи — не сжиматься в столбик из отдельных букв. */}
          <Stack gap={0} minW={0} flex="1 1 150px">
            <Text textStyle="title">{endedText}</Text>
            <Text textStyle="caption" color="fg.muted" lineClamp={1}>
              {peerName}
              {call.acceptedAt ? ` · ${lastDurationRef.current}` : ''}
            </Text>
          </Stack>
          <HStack gap={2} flexShrink={0} ml="auto">
            <Button
              size="sm"
              h="38px"
              variant="outline"
              borderRadius="xl"
              flexShrink={0}
              onClick={() => {
                // Допуск — из кеша шапки переписки, без запроса: сеть до `start` съела бы жест,
                // в котором он спрашивает микрофон. Нет ответа в кеше — решит сервер на старте.
                const reach = queryClient.getQueryData<CallReachAction>(
                  callReachQueryKey(call.peer.userId)
                );
                if (reach && reach !== 'CALL') {
                  toaster.create({ title: t`You can't call this player right now`, type: 'info' });
                  return;
                }
                void start(call.peer, { video: call.withVideo });
              }}
            >
              {t`Call back`}
            </Button>
            {/* Второй выход — туда, откуда звонок увёл. Кнопка акцентная, потому что это
                восстановление отобранного, а не ещё одна попытка дозвониться. */}
            {interruptedVoice && (
              <Button
                size="sm"
                h="38px"
                colorPalette="purple"
                borderRadius="xl"
                flexShrink={0}
                onClick={() => {
                  const target = interruptedVoice;
                  forgetInterruptedVoice();
                  useCallStore.getState().reset();
                  void joinVoice(target.sessionId, {
                    gameName: target.gameName,
                    hostName: target.hostName,
                  }).catch(() => {
                    // Комната могла закрыться, пока шёл звонок. Код отказа рисует карточка
                    // сессии, но её на экране нет — значит объяснять обязан тост, иначе нажатие
                    // выглядит как ничего.
                    toaster.create({
                      title: t`Could not rejoin the session voice`,
                      type: 'error',
                      duration: 6000,
                    });
                  });
                }}
              >
                {t`Back to voice`}
              </Button>
            )}
            {/*
              Убрать полосу одним нажатием. Раньше выхода не было вовсе: она уходила сама через
              шесть секунд (двенадцать, если звонок увёл из голоса), и всё это время человек ждал,
              глядя на закрытое поле ввода. Крестик стоит последним — это выход, а не действие.
            */}
            <IconButton
              aria-label={t`Hide on this device`}
              size="sm"
              minW="32px"
              h="38px"
              bg="transparent"
              color="fg.muted"
              borderRadius="xl"
              flexShrink={0}
              _hover={{ bg: 'bg.emphasized', color: 'fg' }}
              // То же, что делает таймер автоскрытия, только сразу: звонок уже кончился,
              // прятать его «на этом устройстве» не от чего.
              onClick={() => useCallStore.getState().reset()}
            >
              <CloseIcon />
            </IconButton>
          </HStack>
        </HStack>
      )}
    </Portal>
  );
};
